(() => {
  const elementName = 'rlaarlo-feature-explorer';

  if (customElements.get(elementName)) return;

  class RlaarloFeatureExplorer extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToExplorer) return;

      this.isConnectedToExplorer = true;
      this.abortController = new AbortController();
      this.featureCache = new Map();
      this.collectElements();

      if (!this.features.length || !this.detailPanel || !this.detailContentHost) {
        this.teardown();
        return;
      }

      const signal = this.abortController.signal;

      this.addEventListener('click', this.handleClick, { signal });
      this.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
      this.addEventListener('shopify:block:deselect', this.handleBlockDeselect, { signal });
      document.addEventListener('shopify:section:load', this.handleSectionLoad, { signal });
      document.addEventListener('shopify:section:unload', this.handleSectionUnload, { signal });

      if (this.classList.contains('rlaarlo-feature-explorer--full-bleed')) {
        this.updateViewportWidth();
        window.addEventListener('resize', this.handleViewportResize, { signal });
      }

      const initialContent = this.detailContentHost.querySelector('[data-rfe-feature-content]');
      if (initialContent) {
        this.featureCache.set(initialContent.dataset.featureId, initialContent);
      }

      this.currentIndex = Math.max(
        0,
        this.features.findIndex((feature) => feature.id === initialContent?.dataset.featureId)
      );
      this.updateInterface();
      this.setAttribute('data-rfe-ready', 'true');
    }

    disconnectedCallback() {
      this.teardown();
    }

    collectElements() {
      this.detailPanel = this.querySelector('[data-rfe-detail-panel]');
      this.detailContentHost = this.querySelector('[data-rfe-detail-content-host]');
      this.previousButton = this.querySelector('[data-rfe-action="previous"]');
      this.nextButton = this.querySelector('[data-rfe-action="next"]');
      this.status = this.querySelector('[data-rfe-status]');
      this.currentStatus = this.querySelector('[data-rfe-current]');
      this.templates = new Map(
        Array.from(this.querySelectorAll('template[data-rfe-feature-template]')).map((template) => [
          template.dataset.featureId,
          template,
        ])
      );
      const hotspots = new Map(
        Array.from(this.querySelectorAll('[data-rfe-hotspot]')).map((button) => [
          button.dataset.featureId,
          button,
        ])
      );
      this.features = Array.from(this.querySelectorAll('[data-rfe-feature]')).map((feature) => ({
        id: feature.dataset.featureId,
        blockId: feature.dataset.featureBlockId || '',
        title: feature.dataset.featureTitle || '',
        button: hotspots.get(feature.dataset.featureId) || null,
      }));
    }

    handleClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const hotspot = event.target.closest('[data-rfe-hotspot]');
      if (hotspot && this.contains(hotspot)) {
        const index = this.features.findIndex((feature) => feature.id === hotspot.dataset.featureId);
        this.activateFeature(index);
        return;
      }

      const actionButton = event.target.closest('[data-rfe-action]');
      if (!actionButton || !this.contains(actionButton) || actionButton.disabled) return;

      const direction = actionButton.dataset.rfeAction === 'previous' ? -1 : 1;
      const nextIndex =
        (this.currentIndex + direction + this.features.length) % this.features.length;
      this.activateFeature(nextIndex);
    };

    handleBlockSelect = (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;

      const blockId = String(event.detail?.blockId || '');
      let index = this.features.findIndex((feature) => feature.blockId === blockId);

      if (index < 0) {
        const featureElement = event.target.closest('[data-rfe-feature], [data-rfe-hotspot]');
        const featureId = featureElement?.dataset.featureId || '';
        index = this.features.findIndex((feature) => feature.id === featureId);
      }

      if (index < 0) return;

      this.setAttribute('data-rfe-editor-block-selected', 'true');
      this.activateFeature(index);
    };

    handleBlockDeselect = (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;
      this.removeAttribute('data-rfe-editor-block-selected');
    };

    handleSectionLoad = (event) => {
      if (!(event.target instanceof Element) || !event.target.contains(this)) return;
      this.updateViewportWidth();
      this.updateInterface();
    };

    handleSectionUnload = (event) => {
      if (!(event.target instanceof Element) || !event.target.contains(this)) return;
      this.teardown();
    };

    handleViewportResize = () => {
      this.updateViewportWidth();
    };

    updateViewportWidth() {
      if (!this.classList.contains('rlaarlo-feature-explorer--full-bleed')) {
        this.style.removeProperty('--rfe-viewport-width');
        return;
      }

      const viewportWidth = document.documentElement.clientWidth;
      if (viewportWidth > 0) {
        this.style.setProperty('--rfe-viewport-width', `${viewportWidth}px`);
      }
    }

    activateFeature(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.features.length) return;

      const feature = this.features[index];
      let content = this.featureCache.get(feature.id);

      if (!content) {
        const template = this.templates.get(feature.id);
        if (!template) return;

        const fragment = template.content.cloneNode(true);
        content = fragment.querySelector('[data-rfe-feature-content]');
        if (!content) return;

        this.featureCache.set(feature.id, content);
      }

      if (this.detailContentHost.firstElementChild !== content) {
        this.detailContentHost.replaceChildren(content);
      }

      this.currentIndex = index;
      this.updateInterface();
    }

    updateInterface() {
      if (!this.features.length) return;

      this.features.forEach((feature, index) => {
        const isActive = index === this.currentIndex;
        if (feature.button) {
          feature.button.classList.toggle('is-active', isActive);
          feature.button.setAttribute('aria-pressed', String(isActive));
        }
      });

      if (this.previousButton) {
        this.previousButton.disabled = this.features.length <= 1;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.features.length <= 1;
      }

      if (this.currentStatus) {
        this.currentStatus.textContent = String(this.currentIndex + 1);
      }

      if (this.status) {
        const feature = this.features[this.currentIndex];
        const title = feature?.title ? `：${feature.title}` : '';
        this.status.setAttribute(
          'aria-label',
          `当前卖点 ${this.currentIndex + 1}，共 ${this.features.length} 项${title}`
        );
      }
    }

    teardown() {
      if (!this.isConnectedToExplorer) return;

      this.abortController?.abort();
      this.featureCache?.clear();
      this.removeAttribute('data-rfe-ready');
      this.removeAttribute('data-rfe-editor-block-selected');
      this.style.removeProperty('--rfe-viewport-width');
      this.abortController = null;
      this.featureCache = null;
      this.features = [];
      this.templates = null;
      this.detailPanel = null;
      this.detailContentHost = null;
      this.previousButton = null;
      this.nextButton = null;
      this.status = null;
      this.currentStatus = null;
      this.isConnectedToExplorer = false;
    }
  }

  customElements.define(elementName, RlaarloFeatureExplorer);
})();
